import { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  IndianRupee, Layers, MapPin, Users,
  Activity, ChevronLeft, ChevronRight, Home, FileText, Database, ChevronDown, EyeOff, BookOpen, Eye, Heart, Users2
} from "lucide-react";
import { useFilters } from "../context/FilterContext";
import GlobalFilters from "./GlobalFilters";
import axios from "axios";

const MAIN_NAV = [
  { path: "/", label: "Executive Overview", icon: Home },
  { path: "/grant-overview", label: "Grants Overview", icon: Eye },
  { path: "/financial", label: "Financial", icon: IndianRupee },
  { path: "/thematic", label: "Thematic Overview", icon: Layers },
  { path: "/geographic", label: "Geographic Footprint", icon: MapPin },
  { path: "/partners", label: "Implementation Partners", icon: Users2 },
  { path: "/stories", label: "Stories of Change", icon: Heart },
];

const HIDDEN_NAV = [
  { path: "/raw-data", label: "Raw Data", icon: Database },
  { path: "/logics", label: "Logics", icon: BookOpen },
  { path: "/grants", label: "Grants", icon: FileText },
  { path: "/grantee", label: "Grantee Overview", icon: Users },
  { path: "/data-health", label: "Data Health", icon: Activity },
];

const ALL_NAV = [...MAIN_NAV, ...HIDDEN_NAV];

const getPageTitle = (pathname) => {
  const item = ALL_NAV.find(n => n.path === pathname);
  return item?.label || "Dashboard";
};

export default function Layout({ children }) {
  const [collapsed, setCollapsed] = useState(false);
  const [hiddenOpen, setHiddenOpen] = useState(false);
  const [appVersion, setAppVersion] = useState(null);
  const { filterOptions, globalLoading, API } = useFilters();
  const location = useLocation();
  const pageTitle = getPageTitle(location.pathname);

  const isOnHiddenPage = HIDDEN_NAV.some(n => n.path === location.pathname);

  useEffect(() => {
    const fetchMeta = async () => {
      try {
        const base = API.replace(/\/api\/?$/, '');
        const [ver] = await Promise.all([
          axios.get(`${base}/api/version`, { withCredentials: true }),
        ]);
        setAppVersion(ver.data);
      } catch { /* silent */ }
    };
    fetchMeta();
  }, [API]);

  const renderNavItem = (item) => (
    <NavLink
      key={item.path}
      to={item.path}
      data-testid={`nav-${item.path.replace('/', '') || 'home'}`}
      className={({ isActive }) =>
        `flex items-center gap-2 px-2 py-1.5 rounded-md mb-0.5 transition-all duration-150 group ${
          isActive
            ? "bg-red-50 text-[#006837] font-medium border-l-3 border-[#006837]"
            : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
        } ${collapsed ? "justify-center" : ""}`
      }
    >
      {({ isActive }) => (
        <>
          <item.icon size={16} strokeWidth={isActive ? 2 : 1.5} className="flex-shrink-0" />
          {!collapsed && (
            <span className="text-xs truncate">{item.label}</span>
          )}
        </>
      )}
    </NavLink>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50">
      {/* Sidebar */}
      <aside
        data-testid="sidebar"
        className={`flex flex-col bg-white border-r border-slate-200 transition-all duration-300 shadow-sm ${collapsed ? "w-14" : "w-44"}`}
        style={{ flexShrink: 0 }}
      >
        {/* Nav */}
        <nav className="flex-1 py-3 px-1.5 overflow-y-auto">
          {MAIN_NAV.map(renderNavItem)}

          {/* Hidden Pages Group */}
          {!collapsed ? (
            <div className="mt-2">
              <button
                onClick={() => setHiddenOpen(o => !o)}
                data-testid="hidden-pages-toggle"
                className="flex items-center gap-2 px-2 py-1.5 w-full rounded-md text-slate-600 hover:text-slate-600 hover:bg-slate-50 transition-all duration-150"
              >
                <EyeOff size={14} className="flex-shrink-0" />
                <span className="text-[11px] font-medium truncate">Hidden Pages</span>
                <ChevronDown size={12} className={`ml-auto transition-transform duration-200 ${hiddenOpen || isOnHiddenPage ? "rotate-180" : ""}`} />
              </button>
              {(hiddenOpen || isOnHiddenPage) && (
                <div className="ml-1 border-l border-slate-100 pl-1 mt-0.5">
                  {HIDDEN_NAV.map(renderNavItem)}
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="my-2 mx-1 border-t border-slate-100" />
              {HIDDEN_NAV.map(renderNavItem)}
            </>
          )}
        </nav>

        {/* Bottom: collapse toggle only */}
        <div className="border-t border-slate-100 p-2">
          <button
            onClick={() => setCollapsed(c => !c)}
            data-testid="sidebar-collapse-btn"
            className={`flex items-center gap-1.5 w-full px-2 py-1 rounded-md text-[10px] text-slate-500 hover:bg-slate-50 transition-colors ${collapsed ? "justify-center" : ""}`}
          >
            {collapsed ? <ChevronRight size={12} /> : <><ChevronLeft size={12} /><span>Collapse</span></>}
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        {/* Header */}
        <header className="bg-[#006837] text-white px-4 flex items-center justify-between shadow-sm" style={{ height: 44, flexShrink: 0 }}>
          <div className="flex items-center gap-3">
            <span className="text-white text-sm font-bold tracking-wide">Grant Dashboard</span>
            <div className="w-px h-4 bg-red-400/50" />
            <span className="text-red-100 text-xs">Grant Management Dashboard</span>
            <div className="w-px h-4 bg-red-400/50" />
            <span className="text-white text-xs font-medium">{pageTitle}</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-[10px] text-red-200">
              {new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
            </div>
          </div>
        </header>

        {/* Global Filters — hidden on Grants Overview page */}
        {location.pathname !== "/grant-overview" && <GlobalFilters />}

        {/* Loading Bar */}
        {globalLoading && (
          <div className="h-0.5 w-full bg-slate-100 overflow-hidden" data-testid="global-loading-bar">
            <div className="h-full bg-[#006837] animate-loading-bar" />
          </div>
        )}

        {/* Page Content */}
        <main className={`flex-1 overflow-y-auto px-4 py-2 transition-opacity duration-150 ${globalLoading ? "opacity-50 pointer-events-none" : "opacity-100"}`}>
          {children}
        </main>
      </div>
    </div>
  );
}
